import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { TimesheetCalendar } from '../components/TimesheetCalendar';
import api from '../lib/api';
import { toast } from 'sonner';

export default function TimeSheets() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);

  const monthStr = `${year}-${String(month).padStart(2, '0')}`;

  const fetchData = async () => {
    try {
      const res = await api.get(`/timesheets/my?month=${monthStr}`);
      setData(res.data);
    } catch {
      toast.error('Kon time sheets niet laden');
    }
  };
  useEffect(() => { fetchData(); }, [monthStr]);
  useEffect(() => {
    api.get('/timesheets/projects').then(r => setProjects(r.data || [])).catch(() => {});
  }, []);

  const navMonth = (dir) => {
    let m = month + dir, y = year;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setMonth(m); setYear(y);
  };

  const entries = Object.fromEntries((data?.entries || []).map(e => [e.date, e]));
  const canEdit = (ds) => data && ds >= data.editable_from && ds <= data.editable_to;

  const onSave = async (date, start_time, end_time, projectRows) => {
    try {
      const res = await api.post('/timesheets/my', { date, start_time, end_time, projects: projectRows || [] });
      toast.success(`Opgeslagen — ${res.data.hours}u (pauze afgetrokken)`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kon niet opslaan');
      throw err;
    }
  };
  const onDelete = async (date) => {
    try {
      await api.delete(`/timesheets/my/${date}`);
      toast.success('Werkdag verwijderd');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kon niet verwijderen');
    }
  };

  return (
    <div className="space-y-5" data-testid="timesheets-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Time Sheets</h1>
          <p className="text-muted-foreground text-sm">Vink je werkdagen aan met start- en einduur. Alleen de huidige week is aanpasbaar.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navMonth(-1)} data-testid="ts-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="font-medium w-36 text-center capitalize">
            {new Date(year, month - 1).toLocaleDateString('nl-BE', { month: 'long', year: 'numeric' })}
          </span>
          <Button variant="outline" size="icon" onClick={() => navMonth(1)} data-testid="ts-next-month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="lg:col-span-3">
          <CardContent className="pt-5">
            <TimesheetCalendar year={year} month={month} entries={entries} canEdit={canEdit} onSave={onSave} onDelete={onDelete} projects={projects} />
          </CardContent>
        </Card>
        <div className="space-y-4">
          <Card data-testid="ts-month-total">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground text-sm"><Clock className="w-4 h-4" /> Totaal deze maand</div>
              <p className="text-3xl font-bold text-orange-500 mt-1">{data?.total_hours ?? 0}u</p>
              <p className="text-xs text-muted-foreground">{data?.entries?.length || 0} werkdagen</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Per week</CardTitle></CardHeader>
            <CardContent className="space-y-1.5">
              {(data?.weeks || []).length === 0 && <p className="text-sm text-muted-foreground">Nog niets ingevuld.</p>}
              {(data?.weeks || []).map(w => (
                <div key={w.week} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Week {w.week}</span>
                  <span className="font-medium">{w.hours}u</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
